// Imports
import { DateTime } from 'luxon';
import React from 'react';
import ReactDOM from 'react-dom';
import styled, { createGlobalStyle } from 'styled-components/macro';
import { GetAccountsResponse } from '../../../../../../shareables/types/api/main/website/page/type/207/accounts/get';


// Initialize
const printRootIdentifier = 'printable-resident-directory';


// Global style
//  -> The printed document is portaled alongside the app, so hiding every other direct child of the body strips the interface chrome without naming any individual piece of it
const PrintStyle = createGlobalStyle`
	@media print {
		@page {
			margin: 0.5in;
		}
		
		body {
			background: #fff !important;
		}
		
		body > *:not(#${printRootIdentifier}) {
			display: none !important;
		}
	}
`;


// Styled components
const Container = styled.div`
	display: none;
	
	@media print {
		display: block;
		
		color: #000;
		font-size: 0.8125rem;
		line-height: 1.35;
	}
`;

const Masthead = styled.header`
	border-bottom: 0.0625rem solid #000;
	margin-bottom: 0.5rem;
	padding-bottom: 0.25rem;
	break-after: avoid;
`;

const CommunityName = styled.div`
	font-size: 0.75rem;
	letter-spacing: 0.0313rem;
	text-transform: uppercase;
`;

const Title = styled.h1`
	font-size: 1.25rem;
	font-weight: 600;
	line-height: 1.2;
	overflow-wrap: anywhere;
`;

const Summary = styled.div`
	font-size: 0.75rem;
	font-style: italic;
	overflow-wrap: anywhere;
`;

const Entry = styled.div`
	display: flex;
	gap: 0.75rem;
	
	border-bottom: 0.0625rem solid #ccc;
	padding: 0.1875rem 0;
	
	break-inside: avoid;
`;

const Household = styled.div`
	flex: 2 1 0;
	min-width: 0;
	font-weight: 600;
	overflow-wrap: anywhere;
`;

const Addresses = styled.div`
	flex: 3 1 0;
	min-width: 0;
	overflow-wrap: anywhere;
`;


// Define the accepted props
interface Props {
	accounts: GetAccountsResponse;
	communityName: string;
	pageHeader: string;
	searchQuery: string;
}


// Function component
const PrintableDirectory: React.FC<Props> = ({ accounts, communityName, pageHeader, searchQuery }) => {
	// Describe what ended up on the paper
	const householdCount = accounts.length === 1 ? '1 household' : `${accounts.length} households`;
	const printedOn = DateTime.now().toLocaleString(DateTime.DATE_FULL);
	
	
	// Return JSX
	return (
		<React.Fragment>
			<PrintStyle />
			
			{ReactDOM.createPortal(
				<Container id={printRootIdentifier}>
					<Masthead>
						<CommunityName>{communityName}</CommunityName>
						<Title>{pageHeader}</Title>
						
						<Summary>
							{householdCount} · Printed {printedOn}
							{searchQuery.length > 0 && ` · Showing results for “${searchQuery}”`}
						</Summary>
					</Masthead>
					
					{accounts.map((account, i) => (
						<Entry key={i}>
							<Household>{account.consolidatedName}</Household>
							
							<Addresses>
								{account.addresses.length === 0 && <em>No addresses in this community…</em>}
								
								{account.addresses.map((address, addressIndex) => (
									<div key={addressIndex}>{address.formatted}</div>
								))}
							</Addresses>
						</Entry>
					))}
				</Container>,
				document.body
			)}
		</React.Fragment>
	);
};

export default PrintableDirectory;
